import { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { foundationMotionTokens, foundationRadiusTokens, foundationSemantic, foundationSpacingTokens } from '../../foundation';
import { cx } from './utils';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'icon';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: `${foundationSpacingTokens.button.sm} text-xs`,
  md: `${foundationSpacingTokens.button.md} text-sm`,
  lg: `${foundationSpacingTokens.button.lg} text-sm`
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: foundationSemantic.action.primary,
  secondary: foundationSemantic.action.secondary,
  danger: foundationSemantic.action.danger,
  ghost: foundationSemantic.action.ghost,
  icon: foundationSemantic.action.icon
};

const Button: FC<ButtonProps> = ({
  type = 'button',
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  leftIcon,
  rightIcon,
  className,
  children,
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={cx(
        'inline-flex items-center justify-center gap-2 font-medium',
        foundationRadiusTokens.md,
        foundationMotionTokens.colors,
        foundationSemantic.interactive.focusRing,
        foundationSemantic.interactive.disabled,
        variant === 'icon' ? 'h-9 w-9 p-0' : sizeClasses[size],
        variantClasses[variant],
        className
      )}
      disabled={isDisabled}
      aria-busy={loading}
      {...props}
    >
      {loading && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true" />}
      {!loading && leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
};

export default Button;
export type { ButtonProps, ButtonVariant, ButtonSize };
